import { useTransition } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, ClipboardList, User } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { prefetchRoute, type PrefetchableRoute } from "@/lib/routePrefetch";

interface BottomNavProps {
  className?: string;
}

const BottomNav = ({ className }: BottomNavProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fontSize } = useTheme();
  const [navigationPending, startNavigation] = useTransition();

  type Tab = {
    id: string;
    path: string;
    label: string;
    icon: typeof MessageCircle;
    prefetchPath?: PrefetchableRoute;
  };

  const tabs: Tab[] = [
    { id: "chat", path: "/chat", label: "L.IA", icon: MessageCircle, prefetchPath: "/chat" as const },
    { id: "home", path: "/home", label: "Plano", icon: ClipboardList },
    { id: "profile", path: "/profile", label: "Perfil", icon: User, prefetchPath: "/profile" },
  ];

  const isTabActive = (path: string) => location.pathname === path;
  const activeIndex = tabs.findIndex((t) => isTabActive(t.path));

  return (
    <nav
      /* SEM fio no topo — a mesma decisão do header (19/08): a barra não é um
         bloco separado, é o fim da mesma folha. O `glass-panel` já dá a
         separação por matéria (desfoque), que é o que o sistema usa no escuro
         em vez de linha.

         ⚠️ MERGE 21/08: o `main` tinha voltado a `border-t border-border/30`.
         Mantido SEM fio (decisão estética nossa) e mantido o `aria-busy`, que
         é funcional — anuncia navegação em curso pro leitor de tela. */
      className={`fixed bottom-0 left-0 right-0 z-40 glass-panel ${className || ""}`}
      aria-busy={navigationPending || undefined}
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {navigationPending && (
        <span
          role="status"
          aria-label="Abrindo tela"
          className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-primary/70 animate-pulse"
        />
      )}
      <div className="relative flex items-center justify-around h-12">
        {/* Sliding pill */}
        {activeIndex >= 0 && (
          <span
            className="absolute top-1 bottom-1 rounded-full glass-nav-active -z-[1] transition-all duration-300 ease-out"
            style={{
              width: `calc(${100 / tabs.length}% - 24px)`,
              left: `calc(${(activeIndex * 100) / tabs.length}% + 12px)`,
            }}
          />
        )}
        {tabs.map((tab) => {
          const isActive = isTabActive(tab.path);
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`nav-${tab.id}`}
              onPointerEnter={() => {
                if (tab.prefetchPath) void prefetchRoute(tab.prefetchPath);
              }}
              onPointerDown={() => {
                if (tab.prefetchPath) void prefetchRoute(tab.prefetchPath);
              }}
              onFocus={() => {
                if (tab.prefetchPath) void prefetchRoute(tab.prefetchPath);
              }}
              onClick={() => {
                if (!isActive) {
                  if (tab.prefetchPath) void prefetchRoute(tab.prefetchPath);
                  startNavigation(() => {
                    navigate(tab.path, { replace: true });
                  });
                }
              }}
              /* `.tabbar` do sistema (ondiet-brand/tokens/app.css): a aba ativa
                 é ACCENT-TEXT, não a cor do texto. Era `text-foreground` — o
                 mesmo tom do resto da tela, então "onde eu estou" dependia só
                 da pílula de fundo.

                 ⚠️ E ERA `hsl(var(--ring))`, QUE DEIXOU DE SER O ACENTO. Em
                 21/08 o `--ring` virou a hairline forte (#B9B4AD) pra tirar o
                 anel laranja de foco dos campos — e a aba ATIVA foi junto,
                 ficando mais clara que as inativas: quem estava na tela parecia
                 desabilitado. Relatado pelo Pedro em 23/08.

                 Agora aponta pro token do acento direto. `--ring` é foco;
                 acento é `--accent-text`. Usar um pelo outro é como isso
                 quebra em silêncio quando um dos dois muda. */
              className={`relative flex flex-col items-center justify-center gap-0 flex-1 h-full transition-colors duration-200 ${
                isActive ? "text-[color:var(--accent-text)]" : "text-muted-foreground"
              }`}
            >
              <div className="relative">
                <Icon size={16} strokeWidth={1.4} />
              </div>
              <span
                /* INTER, e com a inicial maiúscula só. O mono estava aqui
                   como "rótulo é dado", mas rótulo de NAVEGAÇÃO é palavra que
                   se lê, não valor que se compara — e em caixa alta a barra
                   ficava com dois pesos de voz contra o resto da tela.
                   O 10px e o tracking ficam: eles nasceram do tamanho, não da
                   família. */
                /* ⚠️ SEM opacidade diferente do ícone (✅ Pedro, 23/08: "os
                   ícones e nomes devem ter a mesma cor"). O rótulo inativo
                   ficava a 60% enquanto o ícone ficava a 100% da MESMA cor —
                   dois tons pro mesmo item. Quem distingue ativo de inativo é a
                   cor herdada do botão, e a pílula de fundo. */
                className="text-[10px] font-medium tracking-[0.02em]"
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
